import type { FileTreeNode, DirNode } from "./file-tree";
import {
  comparePaths,
  crossJoin,
  type Path,
  getPath,
  parseFlatRoute,
  type PathSegment,
} from "./flat-routes";
import { createLoc, type Loc, type ParseError } from "./parse";

export interface BuildRoutesResult {
  routes: Route[];
  files: RouteFile[];
  errors: ParseError[];
}

export interface Route {
  path: Path;
  page?: RouteFile;
  handler?: RouteFile;
  layouts: RouteFile[];
  middlewares: RouteFile[];
  metas: RouteFile[];
  partials: RouteFile[];
}

export type RouteFileType =
  | "page"
  | "layout"
  | "handler"
  | "middleware"
  | "meta"
  | "partial";

export interface RouteFile {
  id: string;
  match: RouteFileMatch;
  name: string;
  filePath: string;
  loc: Loc;
}

interface RouteExtra {
  vDir: VDir;
  establishedBy: RouteFile[];
}

type DirNodeLike = Pick<DirNode, "name" | "dir">;

export interface RouteDirMatch {
  type: RouteFileType;
  name: string;
  path: string;
}
export interface RouteFileMatch {
  type: RouteFileType;
  name: string;
  path: string;
  ext: string;
  extra: string;
}

interface VDir {
  depth: number;
  prefix: PathSegment["prefix"];
  parent: VDir | null;
  files?: Map<string, [RouteFile, string]>;
  dirs?: Map<string, VDir>;
  route?: Route;
}

export function buildRoutes(root: FileTreeNode[]): BuildRoutesResult {
  const routes = new Map<string, Route>();
  const errors: ParseError[] = [];
  const vTree: VDir = { depth: 0, parent: null, prefix: "" };
  const files = new Set<RouteFile>();
  const unusedFiles = new Set<RouteFile>();
  const partialNames = new Set<string>();
  let isRoot = true;
  let nextId = 0;
  let dirPath = "/";
  let paths: PathSegment[][] | undefined;

  function traverse(dir: DirNodeLike) {
    const flatRoutes = parseFlatRoute(dir.name, (isRoot = !dir.name));
    if (flatRoutes.errors.length) {
      errors.push(...flatRoutes.errors);
      return;
    }

    let nextDirs: DirNode[] | undefined;
    const routeExtra = new Map<Route, RouteExtra>();
    const nextPaths = flatRoutes.paths.map((path) => path.segments);
    if (paths) {
      try {
        paths = crossJoin(paths, nextPaths);
      } catch (err) {
        errors.push({
          message: (err as Error).message,
          src: dir.name,
          loc: createLoc(0, 0),
        });
        return;
      }
    } else {
      paths = nextPaths;
    }
    dirPath = joinPath(dirPath, dir.name);

    for (const entry of dir.dir) {
      if (entry.dir) {
        const dirMatch = matchRouteDir(entry.name);
        if (dirMatch) {
          const entryDirPath = joinPath(dirPath, entry.name);
          for (const child of entry.dir) {
            if (child.dir) {
              continue;
            }

            const filePath = joinPath(entryDirPath, child.name);

            let match = matchRouteDirFile(dirMatch, child.name);
            if (match) {
              addRouteFile(match, child, filePath, routeExtra);
              continue;
            }

            match = matchRouteFile(child.name);
            if (match) {
              if (
                match.type !== "meta" ||
                !(dirMatch.type === "page" || dirMatch.type === "handler")
              ) {
                errors.push({
                  message: `Cannot nest a +${match.type} file within a +${dirMatch.type} directory`,
                  src: filePath,
                  loc: createLoc(0, 0),
                });
              } else if (match.path) {
                errors.push({
                  message: `Cannot use flat routes within a +${dirMatch.type} directory`,
                  src: filePath,
                  loc: createLoc(0, 0),
                });
              } else {
                match.path = dirMatch.path;
                addRouteFile(match, child, filePath, routeExtra);
              }
            }
          }
        } else if (nextDirs) {
          nextDirs.push(entry);
        } else {
          nextDirs = [entry];
        }
      } else {
        const match = matchRouteFile(entry.name);
        if (match) {
          addRouteFile(match, entry, joinPath(dirPath, entry.name), routeExtra);
        }
      }
    }

    if (nextDirs) {
      const prevDirPath = dirPath;
      const prevPaths = paths;

      for (const dir of nextDirs) {
        traverse(dir);
        dirPath = prevDirPath;
        paths = prevPaths;
      }
    }

    for (const [route, { vDir, establishedBy: establishedBy }] of routeExtra) {
      if (route.page || route.handler) {
        continue;
      }

      let sources = getFileSources(vDir);
      const seen = new Set<RouteFile>();
      const page = getHeritableFile(dirPath, sources, "page");
      const handler = getHeritableFile(dirPath, sources, "handler");
      const meta = getHeritableFile(dirPath, sources, "meta");

      if (page) {
        route.page = page;
        seen.add(page);
      }
      if (handler) {
        route.handler = handler;
        seen.add(handler);
      }
      if (meta) {
        route.metas.push(meta);
        seen.add(meta);
      }

      while (true) {
        const layout = getHeritableFile(dirPath, sources, "layout");
        const middleware = getHeritableFile(dirPath, sources, "middleware");
        if (layout && !seen.has(layout)) {
          route.layouts.push(layout);
          seen.add(layout);
        }
        if (middleware && !seen.has(middleware)) {
          route.middlewares.push(middleware);
          seen.add(middleware);
        }

        if (route.page) {
          for (const name of partialNames) {
            if (!route.partials.find((file) => file.match.name === name)) {
              const partial = getHeritableFile(dirPath, sources, name);
              if (partial && !seen.has(partial)) {
                route.partials.push(partial);
                seen.add(partial);
              }
            }
          }
        }

        const next = sources[sources.length - 1].parent;
        if (!next) break;

        sources = getFileSources(next);
      }

      const routeFile = route.page || route.handler;
      if (routeFile) {
        const existing = routes.get(route.path.key);
        if (!existing || isOverrideRoute(existing, route)) {
          if (route.page) {
            route.layouts.reverse();
          } else {
            route.layouts.length = 0;
          }
          route.middlewares.reverse();
          route.metas.reverse();
          routes.set(route.path.key, route);

          for (const file of routeFileIterator(route)) {
            files.add(file);
            unusedFiles.delete(file);
          }

          if (
            routeFile === vDir.files?.get("page")?.[0] ||
            routeFile === vDir.files?.get("handler")?.[0]
          ) {
            establishedBy[0] = routeFile;
          } else if (establishedBy[0] !== routeFile) {
            establishedBy.unshift(routeFile);
          }
        } else if (!isOverrideRoute(route, existing)) {
          const routeEst = establishedBy.map((file) => file.filePath);
          const existingExtra = routeExtra.get(existing);

          if (existingExtra) {
            const existingEst = existingExtra.establishedBy.map(
              (file) => file.filePath,
            );

            errors.push({
              message: `${routeEst.join(" and ")} establishes route "${route.path.key}" already established by ${existingEst.join(" and ")}`,
              src: routeFile.filePath,
              loc: createLoc(0, 0),
            });
          }
        }
      }
    }
  }

  function addRouteFile(
    match: RouteFileMatch,
    entry: FileTreeNode,
    filePath: string,
    routeExtra: Map<Route, RouteExtra>,
  ) {
    const routeFile: RouteFile = {
      id: `file-${nextId++}`,
      match,
      name: entry.name,
      filePath,
      loc: entry.loc,
    };
    let filePaths = paths!;

    unusedFiles.add(routeFile);
    if (match.type === "partial") {
      partialNames.add(match.name);
    }

    if (match.path) {
      const fileFlatRoute = parseFlatRoute(match.path, isRoot);
      if (fileFlatRoute.errors.length) {
        errors.push(...fileFlatRoute.errors);
        return;
      } else {
        try {
          filePaths = crossJoin(
            paths!,
            fileFlatRoute.paths.map((p) => p.segments),
          );
        } catch (err) {
          errors.push({
            message: (err as Error).message,
            src: match.path,
            loc: createLoc(0, 0),
          });
          return;
        }
      }
    }

    for (const segments of filePaths) {
      const vDir = getVDir(vTree, segments);
      if (!vDir.files) {
        vDir.files = new Map();
      } else {
        const existing = vDir.files.get(match.name);
        if (existing) {
          errors.push({
            message: `File "${routeFile.filePath}" is ambiguous with existing file "${existing[0].filePath}"`,
            src: routeFile.filePath,
            loc: createLoc(0, 0),
          });
          continue;
        }
      }
      vDir.files.set(match.name, [routeFile, dirPath]);

      if (!vDir.route || !routeExtra.has(vDir.route)) {
        routeExtra.set(
          (vDir.route ||= {
            path: getPath(segments),
            layouts: [],
            middlewares: [],
            metas: [],
            partials: [],
          }),
          { vDir, establishedBy: [routeFile] },
        );
      }
    }

    return routeFile;
  }

  traverse({ name: "", dir: root });

  for (const file of unusedFiles) {
    errors.push({
      message: `File "${file.filePath}" was not included in any route`,
      src: file.filePath,
      loc: createLoc(0, 0),
    });
  }

  return {
    routes: [...routes.values()].sort(compareRoutes),
    files: [...files],
    errors,
  };
}

const routeFileRx = /^(.+)?([+@])([^.]+)(\..*)?(\.\w+)$/;
function matchRouteFile(file: string): RouteFileMatch | undefined {
  const match = routeFileRx.exec(file);
  if (match) {
    const [_, path, sep, name, extra, ext] = match;
    const type = sep === "@" ? "partial" : name;
    switch (type) {
      case "page":
      case "layout":
      case "partial":
        if (ext !== ".marko") return;
      case "handler":
      case "middleware":
      case "meta":
        return {
          type,
          path,
          name,
          extra,
          ext,
        };
    }
  }
}

const routeDirRx = /^(.+)?([+@])([^.]+)$/;
function matchRouteDir(file: string): RouteDirMatch | undefined {
  const match = routeDirRx.exec(file);
  if (match) {
    const [_, path, sep, name] = match;
    const type = sep === "@" ? "partial" : name;
    switch (type) {
      case "page":
      case "layout":
      case "partial":
      case "handler":
      case "middleware":
        return {
          type,
          path,
          name,
        };
    }
  }
}

const routeDirFileRx = /^index(\..*)?(\.\w+)$/;
function matchRouteDirFile(
  dir: RouteDirMatch,
  file: string,
): RouteFileMatch | undefined {
  const match = routeDirFileRx.exec(file);
  if (match) {
    const [_, extra, ext] = match;
    switch (dir.type) {
      case "page":
      case "layout":
      case "partial":
        if (ext !== ".marko") return;
      default:
        return {
          ...dir,
          extra,
          ext,
        };
    }
  }
}

function joinPath(base: string, path: string) {
  return path ? (base.endsWith("/") ? base + path : base + "/" + path) : base;
}

function getVDir(root: VDir, segments: PathSegment[]) {
  let cur = root;
  for (const segment of segments) {
    const name = segment.prefix + (segment.dynamic || segment.name);
    cur.dirs ||= new Map();
    let next = cur.dirs.get(name);
    if (!next) {
      cur.dirs.set(
        name,
        (next = {
          depth: cur.depth + 1,
          parent: cur,
          prefix: segment.prefix,
        }),
      );
    }
    cur = next;
  }
  return cur;
}

function isOverrideRoute(base: Route, other: Route) {
  const baseSegments = base.path.segments;
  const otherSegments = other.path.segments;
  const len = baseSegments.length;
  if (otherSegments.length > len) {
    let i = 0;
    while (i < len) {
      if (baseSegments[i].raw !== otherSegments[i].raw) {
        return false;
      }
      i++;
    }
    return otherSegments[i].prefix === "&";
  }
  return false;
}

function getFileSources(vDir: VDir) {
  const sources = [vDir];
  let cur = vDir;
  while (cur.prefix === "&") {
    cur = cur.parent!;
    sources.push(cur);
  }
  return sources;
}

function getHeritableFile(dirPath: string, sources: VDir[], name: string) {
  for (const vDir of sources) {
    const file = vDir.files?.get(name);
    if (file && dirPath.startsWith(file[1])) {
      return file[0];
    }
  }
}

function* routeFileIterator(route: Route) {
  if (route.page) yield route.page;
  if (route.handler) yield route.handler;
  yield* route.metas;
  yield* route.layouts;
  yield* route.middlewares;
  yield* route.partials;
}

function compareRoutes(a: Route, b: Route) {
  return comparePaths(a.path.segments, b.path.segments);
}
